import { useState, useCallback, useRef, useEffect } from 'react';
import { useAvatarAudio } from './useAvatarAudio';


export interface TranscriptItem {
  role: 'user' | 'assistant';
  content: string;
}
const useCallInactivityTimeout = (isActive: boolean, endCall: Function, timeoutMs = 300000) => {
  const timerRef = useRef<null | number>(null);

  // Resets the timeout clock
  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    if (isActive) {
      timerRef.current = setTimeout(() => {
        console.log("Ending call due to innactivity")
        endCall();
      }, timeoutMs);
    }
  }, [isActive, endCall, timeoutMs]);

  useEffect(() => {
    // Monitored interaction events
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];

    if (isActive) {
      // Initialize the timer when call starts
      resetTimer();

      // Attach global listeners for user activity
      events.forEach(event => window.addEventListener(event, resetTimer));
    }

    // Cleanup listeners and timers when call ends or unmounts
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      events.forEach(event => window.removeEventListener(event, resetTimer));
    };
  }, [isActive, resetTimer]);
};

export default function useAgentCall(audioRef?: React.RefObject<HTMLAudioElement | null>, onCallEnd?: (transcript: TranscriptItem[]) => void, sessionId?: string | null, avatarId?: string | null) {
  const [isDialing, setIsDialing] = useState(false);
  const [isCallActive, setIsCallActive] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [phonemas, setPhonemas] = useState([]); // Will hold Piper visemes
  const [chunk] = useState<string[]>([])
  const [transcript] = useState<TranscriptItem[]>([]);
  const [showForm] = useState(false); // Nuevo estado para tu UI
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);

  const { lipsync, connectMediaStream } = useAvatarAudio();
  const onCallEndRef = useRef(onCallEnd);

  const apiUrl = import.meta.env.VITE_API_URL;
  const isDev = import.meta.env.VITE_STAGE === "DEV" ? true : false;
  //const turnServerUrl = import.meta.env.VITE_TURN_URL ? import.meta.env.VITE_TURN_URL : 'turn:turnserver:3478';

  const getIceServes = async (): Promise<RTCIceServer[] | undefined> => {

    try {
      // 1. Send GET request
      const response = await fetch(`${apiUrl}/twilioConfig`);

      // 2. Check if HTTP status code is OK (200-299)
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      // 3. Parse the data stream as JSON
      const data = await response.json();
      console.log(data.ice_servers)
      const ice_servers: RTCIceServer[] = data.ice_servers ? data.ice_servers : [];
      return ice_servers

    } catch (error) {
      // 4. Handle any network or parsing errors
      console.error('Fetching data failed:', error);
    }
  }
  //const apiKey = import.meta.env.VITE_API_KEY;
  //const assistantId = import.meta.env.VITE_ASSISTANT_ID;
  const startCall = useCallback(async () => {
    try {
      console.log(isDev)
      setIsDialing(true)
      // 1. Initialize PC
      // Configuración para el RTCPeerConnection de tu cliente web (React)
      const iceConfig: RTCConfiguration = {
        iceServers: !isDev
          ? await getIceServes()
          : [
            { urls: 'stun:stun.l.google.com:19302' }
          ],
        iceTransportPolicy: isDev ? 'all' : 'relay' // 🔥 CLAVE: Obliga al navegador a usar el proxy TURN y saltarse la NAT de Windows
      };
      const pc = new RTCPeerConnection(iceConfig);
      pcRef.current = pc;


      const dc = pc.createDataChannel("avatar-data");
      dcRef.current = dc;

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          console.log("ICE Candidate added:", event.candidate);
        }
      };

      pc.onconnectionstatechange = () => {
        console.log(`-> Estado global de la conexión: ${pc.connectionState}`);

        if (
          pc.connectionState === "failed" ||
          pc.connectionState === "disconnected" ||
          pc.connectionState === "closed"
        ) {
          endCall();
        }
      };

      // Como respaldo para navegadores antiguos, también escuchamos el estado de ICE
      pc.oniceconnectionstatechange = () => {
        console.log(`-> Estado del transporte ICE: ${pc.iceConnectionState}`);
        if (pc.iceConnectionState === "failed" || pc.iceConnectionState === "disconnected") {
          endCall();
        }
      };
      // 2. Handle Remote Audio (From Python/Piper)

      pc.ontrack = (event) => {
        const [remoteStream] = event.streams;
        if (remoteStream && audioRef?.current) {
          audioRef.current.srcObject = remoteStream;

          connectMediaStream(remoteStream); // Keeps your Wawa-Lipsync working
          audioRef.current.play().catch(console.warn);
        }
      };


      dc.onopen = (e) => {
        console.log(e);
        dc.send(JSON.stringify({ type: "text", value: "Hola" }));
      }
      dc.onmessage = (e) => {
        const event = JSON.parse(e.data);
        console.log(e.data)
        // Handle Phonemes from Piper (Sent by your Python Bridge)
        if (event.type === "visemes") {
          setPhonemas(event.data);
          // You can now pipe event.data to your Three.js morph target logic
        }

        // Handle State
        if (event.type === "state") {
          setIsSpeaking(event.value === "speaking");
        }
      };

      // 4. Add Local Mic (For VAD/STT on backend)
      const localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStream.getTracks().forEach(track => pc.addTrack(track, localStream));

      // 5. Handshake with Local Python Bridge
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      await new Promise((resolve) => {
        if (pc.iceGatheringState === "complete") {
          resolve(true);
        } else {
          function checkState() {
            if (pc.iceGatheringState === "complete") {
              pc.removeEventListener("icegatheringstatechange", checkState);
              resolve(true);
            }
          }
          pc.addEventListener("icegatheringstatechange", checkState);
        }
      });
      // Replace with your local FastAPI URL
      const response = await fetch(`${apiUrl}/offer`, {
        method: "POST",
        body: JSON.stringify({
          sdp: pc.localDescription?.sdp,
          type: pc.localDescription?.type,
          sessionId: sessionId,
          avatarId: avatarId
        }),
        headers: { "Content-Type": "application/json" },
      });

      const answer = await response.json();
      try {
        await pc.setRemoteDescription(new RTCSessionDescription(answer));
      } catch (error) {
        console.error("Failed to set remote description:", error);
        endCall(); // Gracefully end call on failure
      }

      setIsCallActive(true)
      setIsDialing(false)
      console.log("🚀 Connected to Local AI Cluster");


    } catch (error) {
      console.error("Error connecting to Local Bridge:", error);
    }
  }, [connectMediaStream, audioRef, onCallEndRef.current]);

  const toggleMute = useCallback(() => {
    setIsMuted(m => !m);
  }, []);
  const endCall = useCallback(() => {
    if (pcRef.current) {
      pcRef.current.close();
      onCallEndRef.current?.([]);
    }
    pcRef.current = null;
    if (audioRef?.current)
      audioRef.current.srcObject = null;
    setIsCallActive(false);
  }, []);

  useCallInactivityTimeout(isCallActive, endCall, 5 * 60 * 1000);


  return { isCallActive, isMuted, transcript, chunk, phonemas, isSpeaking, startCall, endCall, toggleMute, showForm, isDialing, audioRef, lipsync };
}